import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTerm } from "@/lib/terminology.jsx";
import { Plus } from "lucide-react";
import TaskList from "@/components/TaskList";
import TaskModal from "@/components/TaskModal";
import TaskDetailPanel from "@/components/TaskDetailPanel";

/**
 * v5.13-r2 Deal Tasks tab.
 * Inside a specific deal — Complete/Snooze ALWAYS act on the current deal only,
 * never affecting other linked deals. No picker rendered here.
 */
export default function DealTasksTab({ dealId, pipelineId, isReadOnly }) {
  const term = useTerm();
  const [tasks, setTasks] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);

  // Snooze
  const [snoozeTask, setSnoozeTask] = useState(null);
  const [snoozeUntil, setSnoozeUntil] = useState("");
  // Complete
  const [completeTask, setCompleteTask] = useState(null);
  const [createFollowup, setCreateFollowup] = useState(false);
  const [followupPrefill, setFollowupPrefill] = useState(null);
  const [followupOriginalId, setFollowupOriginalId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/v513/tasks", { params: { deal_id: dealId } });
    setTasks(data);
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const doSnooze = async () => {
    if (!snoozeTask) return;
    if (!snoozeUntil) {
      toast.error("Choose a date");
      return;
    }
    try {
      await api.post(`/v513/tasks/${snoozeTask.task_id}/snooze`, {
        snooze_until: snoozeUntil,
        deal_ids: [dealId],  // always this deal only
      });
      toast.success(`${term("Task")} snoozed for this ${term("deal").toLowerCase()}`);
      setSnoozeTask(null);
      setSnoozeUntil("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const doComplete = async () => {
    if (!completeTask) return;
    try {
      const { data } = await api.post(
        `/v513/tasks/${completeTask.task_id}/complete`,
        {
          create_followup: createFollowup,
          deal_ids: [dealId],  // always this deal only
        }
      );
      toast.success(`${term("Task")} completed for this ${term("deal").toLowerCase()}`);
      const origId = completeTask.task_id;
      setCompleteTask(null);
      if (createFollowup && data?.follow_up_prefill) {
        setFollowupPrefill(data.follow_up_prefill);
        setFollowupOriginalId(origId);
      } else {
        load();
      }
      setCreateFollowup(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="deal-tasks-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-sm text-[var(--cw-muted)]">
            Every {term("task").toLowerCase()} linked to this {term("deal").toLowerCase()} — open
            and completed. Actions here affect this {term("deal").toLowerCase()} only.
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowNew(true)}
            className="cw-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="deal-tasks-new-btn"
          >
            <Plus size={14} /> New {term("Task")}
          </button>
        )}
      </div>

      <TaskList
        tasks={tasks}
        contextDealId={dealId}
        onOpen={(t) => setOpenTaskId(t.task_id)}
        onCompleteClick={(t) => setCompleteTask(t)}
        onSnoozeClick={(t) => setSnoozeTask(t)}
        emptyText={`No ${term("tasks").toLowerCase()} for this ${term("deal").toLowerCase()} yet. Create one above to get started.`}
      />

      {showNew && (
        <TaskModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
          defaultDealId={dealId}
          defaultPipelineId={pipelineId}
        />
      )}

      {openTaskId && (
        <TaskDetailPanel
          taskId={openTaskId}
          contextDealId={dealId}
          onClose={() => setOpenTaskId(null)}
          onChanged={load}
        />
      )}

      {snoozeTask && (
        <Popover
          onClose={() => setSnoozeTask(null)}
          title={`Snooze '${snoozeTask.task_name}' for this ${term("deal").toLowerCase()}?`}
          description={`Only this ${term("deal").toLowerCase()}'s copy of the ${term("task").toLowerCase()} will have its due date pushed. Every other linked ${term("deal").toLowerCase()} is unaffected.`}
          testid="deal-tasks-snooze-popover"
        >
          <div className="cw-caption uppercase text-xs">Snooze until</div>
          <input
            type="date"
            value={snoozeUntil}
            onChange={(e) => setSnoozeUntil(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="deal-tasks-snooze-date"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setSnoozeTask(null)} className="cw-btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={doSnooze}
              className="cw-btn-primary text-sm"
              data-testid="deal-tasks-snooze-confirm"
            >
              Confirm
            </button>
          </div>
        </Popover>
      )}

      {completeTask && (
        <Popover
          onClose={() => setCompleteTask(null)}
          title={`Mark '${completeTask.task_name}' complete for this ${term("deal").toLowerCase()}?`}
          description={`Completing here is permanent for this ${term("deal").toLowerCase()} only. Every other linked ${term("deal").toLowerCase()} stays open.`}
          testid="deal-tasks-complete-popover"
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={createFollowup}
              onChange={(e) => setCreateFollowup(e.target.checked)}
              data-testid="deal-tasks-followup-checkbox"
            />
            Create a follow-up {term("task").toLowerCase()}
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setCompleteTask(null)}
              className="cw-btn-ghost text-sm"
            >
              Cancel
            </button>
            <button
              onClick={doComplete}
              className="cw-btn-primary text-sm"
              data-testid="deal-tasks-complete-confirm"
            >
              Confirm
            </button>
          </div>
        </Popover>
      )}

      {followupPrefill && (
        <TaskModal
          prefill={followupPrefill}
          onClose={() => {
            setFollowupPrefill(null);
            setFollowupOriginalId(null);
            load();
          }}
          onCreated={async (newId) => {
            if (followupOriginalId) {
              try {
                await api.post(`/v513/tasks/${followupOriginalId}/log-followup`, {
                  new_task_id: newId,
                });
              } catch {
                /* non-fatal */
              }
            }
            setFollowupPrefill(null);
            setFollowupOriginalId(null);
            load();
          }}
          title={`Follow-up ${term("Task")}`}
        />
      )}
    </div>
  );
}

function Popover({ onClose, title, description, children, testid }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-white rounded-xl w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="cw-h2 text-base">{title}</h3>
        {description && (
          <div className="text-xs text-[var(--cw-muted)] mt-1">{description}</div>
        )}
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
